import React from "react";
import * as Icons from "lucide-react";
import { useData } from "@/context/DataContext";
import { CompactMatterRow } from "@/components/MatterCard";
import {
  getMatterDoc, seccionVivienda, seccionPersona, seccionVehiculo,
  VIVIENDA_SECCIONES, PERSONA_SECCIONES, VEHICULO_SECCIONES,
} from "@/lib/clasificacion";

const DocRow = ({ doc, onOpen }) => {
  const DIcon = doc.type === "Imagen" ? Icons.Image : Icons.FileText;
  return (
    <button data-testid={`sec-doc-${doc.id}`} onClick={() => onOpen && onOpen(doc)}
      className="w-full flex items-center gap-3 bg-white border border-[#E5DFD5] rounded-xl p-3 text-left hover:border-[#3A6B56] transition-colors">
      <div className="w-9 h-9 rounded-lg bg-[#E8F0EC] flex items-center justify-center shrink-0"><DIcon size={18} className="text-[#3A6B56]" /></div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-sm text-[#1C2421] truncate">{doc.filename}</p>
        <p className="text-xs text-[#6E7A74]">{doc.date_label}</p>
      </div>
      <Icons.Eye size={16} className="text-[#4A5550] shrink-0" />
    </button>
  );
};

// Agrupa asuntos (y documentos sueltos sin asunto) en los 4 apartados.
export const MatterSecciones = ({
  matters, docs = [], scheme = "vivienda", onOpenDoc,
  emptyText = "Sin asuntos todavía.", testidPrefix = "sec",
}) => {
  const { documents } = useData();
  const secciones = scheme === "persona" ? PERSONA_SECCIONES : scheme === "vehiculo" ? VEHICULO_SECCIONES : VIVIENDA_SECCIONES;
  const clasificar = scheme === "persona" ? seccionPersona : scheme === "vehiculo" ? seccionVehiculo : seccionVivienda;

  const bucket = {};
  secciones.forEach((s) => (bucket[s.id] = { matters: [], docs: [] }));
  (matters || []).forEach((m) => {
    const id = clasificar(m, getMatterDoc(m, documents));
    (bucket[id] || bucket.varios).matters.push(m);
  });
  (docs || []).forEach((d) => {
    const id = clasificar(null, d);
    (bucket[id] || bucket.varios).docs.push(d);
  });

  const total = (matters || []).length + (docs || []).length;
  if (total === 0) {
    return (
      <p className="text-base text-[#6E7A74] bg-white border border-[#E5DFD5] rounded-xl p-4" data-testid={`${testidPrefix}-empty`}>
        {emptyText}
      </p>
    );
  }

  return (
    <div className="space-y-4" data-testid={`${testidPrefix}-root`}>
      {secciones.map((s) => {
        const b = bucket[s.id];
        const n = b.matters.length + b.docs.length;
        if (n === 0) return null;
        const Icon = Icons[s.icon] || Icons.Folder;
        return (
          <section key={s.id} data-testid={`${testidPrefix}-${s.id}`}>
            <div className="flex items-center gap-2 mb-2.5">
              <div className="w-8 h-8 rounded-lg bg-[#E8F0EC] flex items-center justify-center shrink-0">
                <Icon size={17} className="text-[#3A6B56]" />
              </div>
              <h3 className="font-heading font-bold text-base text-[#1C2421]">{s.label}</h3>
              <span className="text-xs font-bold text-[#6E7A74] bg-[#F2EDE4] rounded-full px-2 py-0.5">{n}</span>
            </div>
            <div className="space-y-2.5">
              {b.matters.map((m, i) => <CompactMatterRow key={m.id} matter={m} index={i} />)}
              {b.docs.map((d) => <DocRow key={d.id} doc={d} onOpen={onOpenDoc} />)}
            </div>
          </section>
        );
      })}
    </div>
  );
};
